import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { isMobile } from "react-device-detect";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { ReactSVG } from "react-svg";
import classnames from "classnames";
import PropTypes from "prop-types";

const BOX_HEIGHT = isMobile ? 350 : 600;

const useStyles = makeStyles((theme) => ({
  heroContainer: {
    position: "relative",
    height: BOX_HEIGHT,
    width: "100%",
    backgroundColor: "#F4F7FC",
  },
  heroBackground: {
    position: "absolute",
    width: "100%",
    "& > div": {
      display: "flex",
      justifyContent: "center",
      position: "relative",
      height: BOX_HEIGHT,
      overflow: "hidden",
      "& svg": {
        position: "absolute",
        height: "130%",
        top: "-15%",
      },
    },
  },
  heroImageContainer: {
    position: "absolute",
    textAlign: "center",
    height: "100%",
    width: "100%",
  },
  heroImage: {
    height: "100%",
    marginLeft: "-50%",
    marginRight: "-50%",
  },
  heroGradient: {
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(180deg, rgba(13,36,76,0.3) 0%, rgba(13,36,76,0.6) 100%)",
    position: "absolute",
  },
  heroContent: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    height: BOX_HEIGHT,
    padding: theme.spacing(4),
    boxSizing: "border-box",
  },
  heroSubTitleContainer: {
    position: "absolute",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    bottom: 35,
    textTransform: "uppercase",
    animation: `$float 2000ms ${theme.transitions.easing.easeInOut} infinite`,
    cursor: "pointer",
  },
  heroSubTitle: {
    fontSize: "18px",
    width: "100%",
    fontFamily: "Montserrat",
    color: theme.palette.common.white,
    letterSpacing: 5,
    fontWeight: 300,
    textAlign: "center",
  },
  heroSubTitleArrow: {
    position: "relative",
    top: 7,
    margin: "0 auto",
    width: 14,
    height: 14,
    borderColor: theme.palette.common.white,
    borderStyle: "solid",
    borderWidth: 0,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    transform: "rotate(-45deg)",
    cursor: "pointer",
  },
  "@keyframes float": {
    "25%": {
      bottom: 35,
    },
    "75%": {
      bottom: 25,
    },
  },
}));

const Hero = ({ children, className, imgSrc, subTitle, onArrowClick }) => {
  const classes = useStyles();
  const data = useStaticQuery(graphql`
    {
      logo: file(name: { eq: "N_logo" }) {
        publicURL
      }
    }
  `);

  return (
    <Box className={classnames(classes.heroContainer, className)}>
      <ReactSVG src={data.logo.publicURL} className={classes.heroBackground} />
      <Box className={classes.heroImageContainer}>
        <img
          alt="hero"
          {...imgSrc}
          className={classnames(classes.heroImage, imgSrc.className)}
        />
      </Box>
      <div className={classes.heroGradient} />
      <Box className={classes.heroContent}>{children}</Box>
      <Box className={classes.heroSubTitleContainer} onClick={onArrowClick}>
        <Typography className={classes.heroSubTitle}>{subTitle}</Typography>
        <div className={classes.heroSubTitleArrow} />
      </Box>
    </Box>
  );
};

Hero.defaultProps = {
  children: null,
  className: "",
  subTitle: "",
  onArrowClick: () => {},
};

Hero.propTypes = {
  children: PropTypes.element,
  className: PropTypes.string,
  imgSrc: PropTypes.shape({
    src: PropTypes.string.isRequired,
    srcSet: PropTypes.string,
    sizes: PropTypes.string,
    className: PropTypes.string,
  }).isRequired,
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onArrowClick: PropTypes.func,
};

export default Hero;
